import useSidebarState from '../hooks/useSidebarState';
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, get, set } from 'firebase/database';
import './Sourcing.css';
import './Home.css';
import Sidebar from './Sidebar';
import SourcingWorkspace from './TalentAgent/SourcingWorkspace';
import TSEPricing from './TSEPricing/TSEPricing';
import TrialStartButton from './Trial/TrialStartButton';
import { useAccessControl } from '../hooks/useAccessControl';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tutorial.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import AppLoading from './AppLoading';
import { useLocation } from 'react-router-dom';

const Sourcing = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [showTrialButton, setShowTrialButton] = useState(false);
  const [trialHistoryChecked, setTrialHistoryChecked] = useState(false);
  const [userUid, setUserUid] = useState(null);
  const [tutorialActive, setTutorialActive] = useState(false);

  // i18n hooks
  const { t, i18n } = useTranslation('sourcing');
  const { isRTL, language } = useLanguage();

  // Use unified access control hook
  const { accessType, loading, productTypes } = useAccessControl();

  // Check if user has TSE access (via TSE subscription, Bundle subscription, or trial)
  const hasTSEAccess = productTypes.includes('tse') || productTypes.includes('bundle');
  const shortlistFullscreen = (hasTSEAccess || tutorialActive) && new URLSearchParams(location.search).has('shortlist');

  useEffect(() => {
    // Load FontAwesome script
    const fontAwesome = document.createElement('script');
    fontAwesome.src = 'https://kit.fontawesome.com/f6e3baed53.js';
    fontAwesome.crossOrigin = 'anonymous';
    document.head.appendChild(fontAwesome);

    // Load Montserrat fonts (400, 500, 600, 700, 900)
    const montserratLink = document.createElement('link');
    montserratLink.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;900&display=swap';
    montserratLink.rel = 'stylesheet';
    document.head.appendChild(montserratLink);
  }, []);

  // Resolve user UID (for tutorial)
  useEffect(() => {
    const resolveUserUID = async () => {
      if (!currentUser) {
        setUserUid(null);
        return;
      }

      let uid = currentUser.uid;
      try {
        // Check if user is a collaborator
        const collaboratorRef = ref(database, `collaborators/${currentUser.uid}`);
        const collaboratorSnapshot = await get(collaboratorRef);

        if (collaboratorSnapshot.exists()) {
          const collaboratorData = collaboratorSnapshot.val() || {};
          uid = collaboratorData.companyId || collaboratorData.company_uid || currentUser.uid;
        }
        setUserUid(uid);
      } catch (error) {
        console.error('Error resolving user UID:', error);
        setUserUid(currentUser.uid);
      }
    };

    resolveUserUID();
  }, [currentUser]);

  // Check trial history to determine if we should show trial button
  useEffect(() => {
    const checkTrialHistory = async () => {
      if (!currentUser) {
        setShowTrialButton(true); // Not logged in, show trial button
        setTrialHistoryChecked(true);
        return;
      }

      let userUID = currentUser.uid;

      try {
        // Check if user is a collaborator
        const collaboratorRef = ref(database, `collaborators/${currentUser.uid}`);
        const collaboratorSnapshot = await get(collaboratorRef);

        if (collaboratorSnapshot.exists()) {
          const collaboratorData = collaboratorSnapshot.val() || {};
          userUID = collaboratorData.companyId || collaboratorData.company_uid || currentUser.uid;
        }

        // ✅ NEW: Check if user has EVER purchased any plan
        const subscriptionRef = ref(database, `companies/${userUID}/subscription`);
        const subscriptionSnapshot = await get(subscriptionRef);

        if (subscriptionSnapshot.exists()) {
          // User has purchased before (or currently has subscription) - NO TRIAL
          console.log('[Sourcing] User has purchase history - hiding trial button');
          setShowTrialButton(false);
          setTrialHistoryChecked(true);
          return;
        }

        // Check trial history to see if they already used it
        const trialHistoryRef = ref(database, `companies/${userUID}/trial_history`);
        const trialHistorySnapshot = await get(trialHistoryRef);

        if (trialHistorySnapshot.exists()) {
          const historyData = trialHistorySnapshot.val();
          if (historyData.hadTrial === true) {
            // User already used their trial - don't show trial button
            setShowTrialButton(false);
            setTrialHistoryChecked(true);
            return;
          }
        }

        // Check if trial is currently active or expired
        const trialRef = ref(database, `companies/${userUID}/trial`);
        const trialSnapshot = await get(trialRef);

        if (trialSnapshot.exists()) {
          // User has trial data (active or expired) - don't show trial button
          setShowTrialButton(false);
        } else {
          // No trial history, no trial data, no purchase - show trial button
          setShowTrialButton(true);
        }

      } catch (error) {
        console.error('Error checking trial history:', error);
        setShowTrialButton(false);
      } finally {
        setTrialHistoryChecked(true);
      }
    };

    checkTrialHistory();
  }, [currentUser]);

  // TSE Tutorial function - matches TalentSearch implementation exactly
  const startTSETutorial = useCallback(() => {
    const isArabic = isRTL || language === 'ar' || (i18n?.language || '').startsWith('ar');
    const tr = (en, ar) => (isArabic ? ar : en);
    // Mark tutorial as active - this will show TalentSearch instead of pricing
    setTutorialActive(true);

    const steps = [
      {
        popover: {
          title: tr('👋 Welcome to the Talent Search Engine!', '👋 أهلاً بك في محرك البحث عن المواهب'),
          description: tr(
            `
            Search through <strong>800M+ verified candidate profiles</strong> using AI-powered search.<br/><br/>
            <strong>Let's explore the key features!</strong>
          `,
            `
            ابحث في <strong>أكثر من 800M ملف مرشح موثّق</strong> باستخدام بحث مدعوم بالذكاء الاصطناعي.<br/><br/>
            <strong>لنستكشف المزايا الأساسية!</strong>
          `
          ),
        }
      },
      {
        element: '.search-box',
        popover: {
          title: tr('🔍 Smart Search', '🔍 بحث ذكي'),
          description: tr(
            `
            <strong>Two ways to search:</strong><br/>
            1. <strong>Type keywords</strong> - "Software Engineer with React experience"<br/>
            2. <strong>Paste a job description</strong> - AI finds the best matches<br/><br/>
            The AI understands context and finds the best matching profiles for you
          `,
            `
            <strong>طريقتان للبحث:</strong><br/>
            1. <strong>أدخل كلمات مفتاحية</strong> – مثل "مهندس برمجيات بخبرة React"<br/>
            2. <strong>ألصق وصف الوظيفة</strong> – والذكاء الاصطناعي سيجد أفضل المرشحين<br/><br/>
            الذكاء الاصطناعي يفهم السياق ويختار أنسب الملفات لك.
          `
          ),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '.profile-card',
        popover: {
          title: tr('👤 Profile Cards', '👤 بطاقات الملف الشخصي'),
          description: tr(
            `
            Each card shows:<br/>
            • Candidate name & headline<br/>
            • Experience summary<br/>
            • Skills & tags<br/>
            • "Open to Work" status<br/>
            • Ranking badge <br/><br/>
            <strong>Click any card</strong> to view full profile
          `,
            `
            كل بطاقة تعرض:<br/>
            • اسم المرشح والعنوان المهني<br/>
            • ملخص الخبرة<br/>
            • المهارات والوسوم<br/>
            • حالة "متاح للعمل"<br/>
            • شارة الترتيب<br/><br/>
            <strong>انقر على أي بطاقة</strong> لعرض الملف الكامل.
          `
          ),
          side: 'top',
          align: 'start'
        }
      },
      {
        element: '.hamburger-icon',
        popover: {
          title: tr('📋 Shortlists', '📋 القوائم المختصرة'),
          description: tr(
            `
            <strong>Save candidates into custom lists:</strong><br/>
            • Create multiple shortlists (e.g., "Top Engineers", "Final Round")<br/>
            • Add candidates to shortlists with one click<br/>
            • View and manage shortlisted profiles<br/><br/>
            <strong>Pro tip:</strong> Use credits wisely by unlocking only shortlisted candidates
          `,
            `
            <strong>احفظ المرشحين في قوائم مخصّصة:</strong><br/>
            • أنشئ عدة قوائم (مثل "أفضل المهندسين"، "المقابلة النهائية")<br/>
            • أضف المرشحين للقوائم بنقرة واحدة<br/>
            • اعرض وأدر الملفات المختارة<br/><br/>
            <strong>نصيحة:</strong> استخدم الأرصدة بحكمة وافتح ملفات المرشحين الموجودين في القوائم فقط.
          `
          ),
          side: 'left',
          align: 'start'
        }
      },
      {
        element: '.credits-display',
        popover: {
          title: tr('⚡ Credit System', '⚡ نظام الأرصدة'),
          description: tr(
            `
            <strong>How credits work:</strong><br/>
            • Each profile unlock costs 1 credit<br/>
            • Unlocking reveals full CV & contact info<br/>
            • Credits are shared across your team<br/>
            • Purchase more credits anytime<br/><br/>
            <strong>View unlocked profiles</strong> by clicking "View Unlocked Profiles"
          `,
            `
            <strong>كيف تعمل الأرصدة:</strong><br/>
            • فتح كل ملف يكلف 1 رصيد<br/>
            • الفتح يكشف السيرة الذاتية كاملة وبيانات التواصل<br/>
            • الأرصدة مشتركة مع فريقك<br/>
            • يمكنك شراء أرصدة إضافية في أي وقت<br/><br/>
            <strong>اعرض الملفات المفتوحة</strong> بالضغط على "View Unlocked Profiles".
          `
          ),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        popover: {
          title: tr('🎉 TSE Complete!', '🎉 انتهى دليل محرك البحث'),
          description: tr(
            `
            <strong>You now know how to:</strong><br/>
            ✅ Search 800M+ profiles with AI<br/>
            ✅ View candidate cards & details<br/>
            ✅ Create and manage shortlists<br/>
            ✅ Unlock profiles with credits<br/><br/>
            <strong>Ready to see Onboarding?</strong><br/>
            <em>(Automate new hire onboarding workflow)</em>
          `,
            `
            <strong>أصبحت تعرف كيف:</strong><br/>
            ✅ تبحث في أكثر من 800M ملف عبر الذكاء الاصطناعي<br/>
            ✅ تعرض بطاقات المرشحين والتفاصيل<br/>
            ✅ تنشئ القوائم المختصرة وتديرها<br/>
            ✅ تفتح الملفات باستخدام الأرصدة<br/><br/>
            <strong>مستعد لعرض الإعداد الوظيفي؟</strong><br/>
            <em>(أتمتة مهام استقبال المنضمين الجدد)</em>
          `
          ),
        }
      }
    ];

    let tutorialCompleted = false;

    const driverObj = driver({
      showProgress: true,
      steps: steps,
      allowClose: true,
      onDestroyStarted: () => {
        // Tutorial is being destroyed
        setTutorialActive(false);
      },
      onDestroyed: async () => {
        // Tutorial ended - check if it was completed or quit early
        setTutorialActive(false);

        // Save progress only if tutorial was completed (reached last step)
        if (tutorialCompleted && userUid) {
          try {
            await set(ref(database, `companies/${userUid}/tutorialProgress/tse`), true);
          } catch (error) {
            console.error('Error saving tutorial progress:', error);
          }
        }

        // If completed, ask about Onboarding tutorial
        if (tutorialCompleted) {
          const continueToOnboarding = window.confirm(
            tr(
              "Ready to see the Onboarding module?\n\nAutomate your new hire onboarding with task tracking, document signing, and team assignments.\n\nTakes 60 seconds.",
              "هل أنت مستعد لرؤية قسم الإعداد الوظيفي؟\n\nأتمت مهام انضمام الموظفين الجدد مع تتبع المهام، توقيع المستندات، وتوزيع المسؤوليات.\n\nيستغرق 60 ثانية."
            )
          );

          if (continueToOnboarding) {
            window.location.href = '/employee-onboarding?startTutorial=true';
            return;
          }
        }

        // Reload page to restore pricing screen (always reload)
        window.location.reload();
      },
      onHighlightStarted: (element, step) => {
        // Mark as completed when reaching the last step
        if (step && step.index === steps.length - 1) {
          tutorialCompleted = true;
        }
      }
    });

    driverObj.drive();
  }, [userUid]);

  // Listen for tutorial trigger from ProductProtectedRoute overlay
  useEffect(() => {
    const handleTutorialFromOverlay = (event) => {
      console.log('[Sourcing] Received startTutorialFromOverlay event', event.detail);

      // Start the TSE tutorial
      startTSETutorial();
    };

    window.addEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    return () => {
      window.removeEventListener('startTutorialFromOverlay', handleTutorialFromOverlay);
    };
  }, [startTSETutorial]);

  if (loading || !trialHistoryChecked) {
    return <AppLoading />;
  }

  console.log('[Sourcing Debug]', { hasTSEAccess, showTrialButton, trialHistoryChecked, productTypes, accessType });

  return (
    <div style={{ height: '100vh' }}>
      {!shortlistFullscreen && <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />}
      <div style={{
        marginInlineStart: shortlistFullscreen ? 0 : sidebarCollapsed ? '60px' : '240px',
        transition: 'margin-inline-start 250ms ease',
        height: '100vh',
        width: shortlistFullscreen ? '100vw' : `calc(100vw - ${sidebarCollapsed ? '60px' : '240px'})`,
        boxSizing: 'border-box',
        overflow: 'auto'
      }}>
        {(hasTSEAccess || tutorialActive) ? (
          // ✅ User has TSE access OR tutorial is active (show TalentSearch for tutorial)
          <SourcingWorkspace tutorialActive={tutorialActive} />
        ) : (
          // ❌ User does NOT have TSE access and tutorial not active
          showTrialButton ? (
            // User hasn't used trial yet - show trial activation
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: 'var(--app-background, #031128)'
            }}>
              <div className="modal-content" style={{
                maxWidth: '500px',
                textAlign: 'center',
                padding: '40px'
              }}>
                <div className="modal-header" style={{
                  borderBottom: 'none',
                  paddingBottom: '0',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <h2 style={{ fontSize: '28px', marginBottom: '8px' }}>{t('trial.title', 'Start Your Free Trial')}</h2>
                  <p style={{
                    fontSize: '16px',
                    color: '#666',
                    margin: '0 0 24px 0',
                    fontWeight: 'normal'
                  }}>
                    {t('trial.description', 'Get 14 days of full access to ATS + 10 TSE test credits.')}
                    <br />
                    {t('trial.noCreditCard', 'No credit card required!')}
                  </p>
                </div>
                <div className="form-section" style={{
                  padding: '24px 0 0 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0'
                }}>
                  <TrialStartButton />
                </div>
              </div>
            </div>
          ) : (
            // User already used trial or no trial available - show pricing page
            <TSEPricing />
          )
        )}
      </div>
    </div>
  );
};

export default Sourcing;
